import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Platform } from 'react-native';
import { Text, IconButton, Divider, List, useTheme } from 'react-native-paper';
import { useClient } from '../providers/ClientProvider';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParams } from './Home';
import { useNav } from '../providers/NavProvider';
import { useAuth } from '../providers/AuthProvider';
import { getObjectByPath } from '../services/Utils';
import { ISitee } from './ClientLanding';
import { useKpiConfig } from './Logs/Dashboard/kpi/useKpiConfig';
import { SingleSelectDropdown } from './Logs/common/controls/Dropdown-filter';
import { useFocusEffect } from '@react-navigation/native';
import Header from './Header';
import LogProvider from './Logs/LogProvider';
import Animated, {
    useSharedValue,
    useAnimatedStyle,
    withSpring
} from 'react-native-reanimated';
import ReportProvider from './Logs/ReportProvider';

type Props = NativeStackScreenProps<RootStackParams, "PortfolioSideNav">

interface GroupedNav {
    categoryName: string;
    lists: ISitee[];
}

const EXCLUDED_LISTS = [
    'Permits And Licenses',
    'Permit And Licenses',
    'Report Request',
    'Contracts',
    'Permit Register',
    'Package Status',
    'Project Records',
    'Documents'
];

const PortfolioSideNav: React.FC<Props> = ({ route }) => {
    const theme = useTheme();
    const { setCurrentClientShortURL, clientAPIURL } = useClient();
    const { sitesResp, setCurrentLeafObj } = useNav();
    const { getAccessToken } = useAuth();
    const [siteObj, setSiteObj] = React.useState<ISitee>(null);
    const [selectedList, setSelectedList] = useState<ISitee | null>(null);

    const [selectedProject, setSelectedProject] = useState<{ id: string | number, title: string }>({ id: 'all', title: 'All Projects' });
    const [selectedPackages, setSelectedPackages] = useState<string[]>([]);

    useEffect(() => { setSelectedPackages([]); }, [selectedProject.id]);

    const [kpiAccessToken, setKpiAccessToken] = useState<string | null>(null);
    useEffect(() => { getAccessToken().then(setKpiAccessToken); }, []);
    const kpiConfig = useKpiConfig(clientAPIURL, kpiAccessToken, selectedProject.id === 'all' ? null : String(selectedProject.id));

    const packageOptions = useMemo(() => {
        if (!sitesResp || selectedProject.id === 'all') return [];
        const excluded = kpiConfig.excludedPackageKeywords.map((k: string) => k.toLowerCase());
        const site = (sitesResp.children || []).find((c: ISitee) => c.id === selectedProject.id);
        if (!site) return [];
        return (site.children || [])
            .filter((c: ISitee) =>
                (c.path || '').split('-').length === 3 &&
                !excluded.some((k: string) => (c.title || '').toLowerCase().includes(k))
            )
            .sort((a: ISitee, b: ISitee) => a.title.localeCompare(b.title))
            .map((p: ISitee) => ({ label: p.title, value: p.title }));
    }, [sitesResp, selectedProject.id, kpiConfig.excludedPackageKeywords]);

    // Sidebar State
    const [isLocked, setIsLocked] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const sidebarWidth = useSharedValue(60);

    const sidebarStyle = useAnimatedStyle(() => ({
        width: sidebarWidth.value,
    }));

    const updateSidebarWidth = (expanded: boolean) => {
        sidebarWidth.value = withSpring(expanded ? 280 : 60, { damping: 15 });
    };

    const toggleExpand = () => {
        if (!isLocked) {
            const next = !isExpanded;
            setIsExpanded(next);
            updateSidebarWidth(next);
        }
    };

    const toggleLock = () => {
        const next = !isLocked;
        setIsLocked(next);
    };

    const handleMouseEnter = () => {
        if (!isLocked && !isExpanded) {
            setIsExpanded(true);
            updateSidebarWidth(true);
        }
    };

    const handleMouseLeave = () => {
        if (!isLocked && isExpanded) {
            setIsExpanded(false);
            updateSidebarWidth(false);
        }
    };

    const extractGroupedData = useCallback((node: ISitee) => {
        const categoriesMap = new Map<string, Set<string>>();
        const listObjectsMap = new Map<string, ISitee>();

        const traverse = (current: ISitee, parentTitle: string) => {
            if (current.type === 'list') {
                const isExcluded = EXCLUDED_LISTS.some(excludedTitle =>
                    current.title.toLowerCase().trim() === excludedTitle.toLowerCase().trim()
                );

                if (!isExcluded) {
                    if (!categoriesMap.has(parentTitle)) {
                        categoriesMap.set(parentTitle, new Set());
                    }
                    categoriesMap.get(parentTitle).add(current.title);
                    if (!listObjectsMap.has(current.title)) {
                        listObjectsMap.set(current.title, current);
                    }
                }
            }
            if (current.children) {
                current.children.forEach(child => traverse(child, current.title));
            }
        };

        if (node) {
            traverse(node, "General");
        }

        const result: GroupedNav[] = [];
        categoriesMap.forEach((listTitles, catName) => {
            const lists = Array.from(listTitles).map(title => listObjectsMap.get(title));
            result.push({ categoryName: catName, lists });
        });

        return result;
    }, []);

    // Memoized Navigation and Project data to prevent redundant re-renders
    const groupedNav = React.useMemo(() => {
        if (!sitesResp) return [];
        return extractGroupedData(sitesResp);
    }, [sitesResp, extractGroupedData]);

    const projects = React.useMemo(() => {
        if (!sitesResp || !sitesResp.children) return [];
        return sitesResp.children.filter(c => c.type === 'site');
    }, [sitesResp]);

    const projectOptions = useMemo(() => [
        { label: 'All Projects', value: 'all' },
        ...projects.map(p => ({ label: p.title, value: p.id.toString() })),
    ], [projects]);

    useFocusEffect(
        React.useCallback(() => {
            if (groupedNav.length > 0 && !selectedList) {
                if (groupedNav[0].lists.length > 0) {
                    setSelectedList(groupedNav[0].lists[0]);
                }
            }
            if (siteObj) {
                setCurrentLeafObj(siteObj);
            }
        }, [groupedNav, selectedList, siteObj, setCurrentLeafObj])
    );

    React.useEffect(() => {
        if (route.params?.clientShortURL) {
            setCurrentClientShortURL(route.params.clientShortURL);
        }
        if (sitesResp && route.params?.sitePath) {
            const tempsiteObj = getObjectByPath(decodeURIComponent(route.params.sitePath), sitesResp);
            setSiteObj(tempsiteObj);
        }
    }, [route?.params?.sitePath, sitesResp]);

    return (
        <View style={styles.root}>
            <Header />
            <View style={styles.mainLayout}>
                <Animated.View
                    {...({
                        onMouseEnter: Platform.OS === 'web' ? handleMouseEnter : undefined,
                        onMouseLeave: Platform.OS === 'web' ? handleMouseLeave : undefined,
                    } as any)}
                    style={[styles.sidebar, sidebarStyle, { backgroundColor: '#ffffff' }]}
                >
                    <View style={styles.sidebarHeader}>
                        <IconButton
                            icon="menu"
                            onPress={toggleExpand}
                            disabled={isLocked}
                            iconColor="#FFFFFF"
                        />
                        {isExpanded && (
                            <>
                                <Text variant="titleMedium" style={styles.sidebarTitle}>Navigation</Text>
                                <IconButton
                                    icon={isLocked ? "pin" : "pin-outline"}
                                    size={20}
                                    onPress={toggleLock}
                                    iconColor={isLocked ? "#4BB75F" : "rgba(255, 255, 255, 0.6)"}
                                />
                            </>
                        )}
                    </View>

                    <Divider />

                    <ScrollView style={styles.sidebarScroll}>
                        {isExpanded && groupedNav.map((group) => (
                            <List.Accordion
                                key={group.categoryName}
                                title={group.categoryName}
                                left={props => <List.Icon {...props} icon="folder-outline" color="#102B27" />}
                                style={styles.accordion}
                                titleStyle={styles.accordionTitle}
                            >
                                {group.lists.map((list) => (
                                    <List.Item
                                        key={list.id}
                                        title={list.title}
                                        onPress={() => setSelectedList(list)}
                                        style={[
                                            styles.listItem,
                                            selectedList?.id === list.id && styles.selectedListItem
                                        ]}
                                        titleStyle={[
                                            styles.listItemText,
                                            selectedList?.id === list.id && styles.selectedListItemText
                                        ]}
                                    />
                                ))}
                            </List.Accordion>
                        ))}
                    </ScrollView>
                </Animated.View>

                <View style={styles.content}>
                    <View style={styles.contentHeader}>
                        <View>
                            <Text variant="headlineSmall" style={styles.pageTitle}>
                                {selectedList ? selectedList.title : "Select a Log"}
                            </Text>
                            <Text variant="bodySmall" style={{ color: theme.colors.outline }}>
                                Portfolio Data Aggregator
                            </Text>
                        </View>

                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                            <View style={{ minWidth: 180 }}>
                                <SingleSelectDropdown
                                    options={projectOptions}
                                    label="All Projects"
                                    selected={selectedProject.id.toString()}
                                    onSelect={(v) => {
                                        const val = v as string;
                                        if (val === 'all') {
                                            setSelectedProject({ id: 'all', title: 'All Projects' });
                                        } else {
                                            const proj = projects.find(p => p.id.toString() === val);
                                            if (proj) setSelectedProject({ id: proj.id, title: proj.title });
                                        }
                                    }}
                                    menuAlign="right"
                                />
                            </View>

                            {selectedProject.id !== 'all' && packageOptions.length > 0 && (
                                <View style={{ minWidth: 180 }}>
                                    <SingleSelectDropdown
                                        options={packageOptions}
                                        label="All Packages"
                                        selected={selectedPackages}
                                        onSelect={(v) => setSelectedPackages(v as string[])}
                                        isMulti
                                        menuAlign="right"
                                    />
                                </View>
                            )}
                        </View>
                    </View>

                    <Divider style={{ marginVertical: 10 }} />

                    <View style={styles.logWrapper}>
                        {(() => {
                            console.log('Rendering ReportProvider. selectedList:', selectedList?.title, 'sitesResp:', !!sitesResp, 'selected Project ID:', selectedProject.id.toString());
                            if (selectedList && sitesResp) {
                                return (
                                    <ReportProvider
                                        key={`${selectedList.id}-${selectedProject.id}`}
                                        logTitle={selectedList.title}
                                        parentId={selectedProject.id === 'all' ? sitesResp.id : selectedProject.id.toString()}
                                        projectId={selectedProject.id === 'all' ? sitesResp.id : selectedProject.id.toString()}
                                        isDashboard={true}
                                        packageFilter={selectedPackages.length === 0 ? null : selectedPackages}
                                    />
                                );
                            }
                            return (
                                <View style={styles.emptyState}>
                                    <Text>Please select a log from the sidebar to view data.</Text>
                                </View>
                            );
                        })()}
                    </View>
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    root: {
        flex: 1,
        backgroundColor: '#f8f9fa',
    },
    mainLayout: {
        flex: 1,
        flexDirection: 'row',
        height: '100%',
    },
    sidebar: {
        borderRightWidth: 1,
        borderRightColor: 'rgba(16, 43, 39, 0.1)', // Subtle border
        shadowColor: '#000',
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 0.05,
        shadowRadius: 10,
        elevation: 10,
        zIndex: 9999,
        overflow: 'hidden',
        height: '100%',
        minWidth: 60,
        backgroundColor: 'rgba(255, 255, 255, 0.95)', // Translucent feel
    },
    sidebarHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        height: 70, // Slightly taller for premium feel
        paddingHorizontal: 12,
        backgroundColor: '#102B27', // Deep Emerald Header
    },
    sidebarTitle: {
        flex: 1,
        marginLeft: 12,
        color: '#FFFFFF',
        fontWeight: '700',
        letterSpacing: 0.5,
    },
    sidebarScroll: {
        flex: 1,
        paddingTop: 10,
    },
    accordion: {
        backgroundColor: 'transparent',
        paddingVertical: 4,
    },
    accordionTitle: {
        fontSize: 15,
        fontWeight: '700',
        color: '#102B27',
    },
    listItem: {
        paddingLeft: 24,
        marginVertical: 4,
        marginHorizontal: 12,
        borderRadius: 12,
        height: 45,
        justifyContent: 'center',
    },
    selectedListItem: {
        backgroundColor: 'rgba(75, 183, 95, 0.08)', // Very light emerald tint
        borderLeftWidth: 4,
        borderLeftColor: '#4BB75F', // Bright premium green
        borderTopRightRadius: 12,
        borderBottomRightRadius: 12,
        borderTopLeftRadius: 4,
        borderBottomLeftRadius: 4,
        // Subtle glow effect
        shadowColor: '#4BB75F',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
    },
    listItemText: {
        fontSize: 14,
        fontWeight: '500',
        color: '#546E7A', // Slate for inactive
    },
    selectedListItemText: {
        color: '#102B27', // Deep Emerald for active
        fontWeight: '700',
    },
    content: {
        flex: 1,
        padding: 30, // More breathing room
        backgroundColor: '#fdfdfd',
    },
    contentHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
    },
    pageTitle: {
        fontWeight: '800',
        color: '#102B27',
        fontSize: 28,
    },
    filterCard: {
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 25,
        backgroundColor: '#ffffff',
        elevation: 4,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
    },
    filterContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    filterText: {
        fontSize: 14,
        fontWeight: '700',
        color: '#102B27',
    },
    logWrapper: {
        flex: 1,
        marginTop: 15,
        borderRadius: 16,
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        // Optional: slight inner shadow or subtle border for the log area
    },
    emptyState: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        opacity: 0.5,
    },
    dropdownModal: {
        backgroundColor: 'white',
        padding: 0,
        width: 300,
        alignSelf: 'center',
        borderRadius: 12,
        maxHeight: '60%',
        overflow: 'hidden',
        elevation: 24,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 12 },
        shadowOpacity: 0.3,
        shadowRadius: 16,
    },
    dropdownScroll: {
        paddingVertical: 8,
    },
    dropdownItem: {
        paddingVertical: 4,
    }
});

export default PortfolioSideNav;
